import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Collapse,
  Card,
  CardContent,
  Grid,
  Paper
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon
} from '@mui/icons-material';

interface Category {
  id: string;
  categoryNameFR: string;
  categoryNameEN: string;
  categoryNameES: string;
  descriptionFR: string;
  descriptionEN: string;
  descriptionES: string;
  subcategories: { [key: string]: SubCategory };
}

interface SubCategory {
  subcategoryName: string;
  prompt: string;
  descriptionFR: string;
  descriptionEN: string;
  descriptionES: string;
}

interface FormData {
  categoryName: string;
  categoryNameEN: string;
  categoryNameES: string;
  descriptionFR: string;
  descriptionEN: string;
  descriptionES: string;
}

export default function CategoryManager() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSubCategoryDialog, setOpenSubCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubCategory, setEditingSubCategory] = useState<{categoryId: string, subCategoryId?: string, data: SubCategory} | null>(null);
  const [formData, setFormData] = useState<FormData>({ 
    categoryName: '', 
    categoryNameEN: '',
    categoryNameES: '',
    descriptionFR: '',
    descriptionEN: '',
    descriptionES: '' 
  });
  const [subCategoryFormData, setSubCategoryFormData] = useState({ 
    name: '', 
    description: '',
    descriptionFR: '',
    descriptionEN: '',
    descriptionES: '' 
  });
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'analysis_categories'));
      const categoriesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          categoryNameFR: data.categoryNameFR || data.categoryName || '', // Rétrocompatibilité
          categoryNameEN: data.categoryNameEN || '',
          categoryNameES: data.categoryNameES || '',
          descriptionFR: data.descriptionFR || data.description || '', // Rétrocompatibilité
          descriptionEN: data.descriptionEN || '',
          descriptionES: data.descriptionES || '',
          subcategories: data.subcategories || {}
        };
      });
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Erreur lors du chargement des catégories");
    }
  };

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        categoryName: category.categoryNameFR || '',
        categoryNameEN: category.categoryNameEN || '',
        categoryNameES: category.categoryNameES || '',
        descriptionFR: category.descriptionFR || '',
        descriptionEN: category.descriptionEN || '',
        descriptionES: category.descriptionES || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({ categoryName: '', categoryNameEN: '', categoryNameES: '', descriptionFR: '', descriptionEN: '', descriptionES: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({ categoryName: '', categoryNameEN: '', categoryNameES: '', descriptionFR: '', descriptionEN: '', descriptionES: '' });
    setError(null);
  };

  const handleOpenSubCategoryDialog = (categoryId: string, subCategoryId?: string, subCategory?: SubCategory) => {
    setEditingSubCategory({
      categoryId,
      subCategoryId,
      data: subCategory || {
        subcategoryName: '',
        prompt: '',
        descriptionFR: '',
        descriptionEN: '',
        descriptionES: ''
      }
    });
    if (subCategory) {
      setSubCategoryFormData({
        name: subCategory.subcategoryName,
        description: subCategory.prompt,
        descriptionFR: subCategory.descriptionFR || '',
        descriptionEN: subCategory.descriptionEN || '',
        descriptionES: subCategory.descriptionES || ''
      });
    } else {
      setSubCategoryFormData({
        name: '',
        description: '',
        descriptionFR: '',
        descriptionEN: '',
        descriptionES: ''
      });
    }
    setOpenSubCategoryDialog(true);
  };

  const handleCloseSubCategoryDialog = () => {
    setOpenSubCategoryDialog(false);
    setEditingSubCategory(null);
    setSubCategoryFormData({
      name: '',
      description: '',
      descriptionFR: '',
      descriptionEN: '',
      descriptionES: ''
    });
  };

  const handleSaveCategory = async () => {
    if (!formData.categoryName.trim()) {
      setError('Le nom de la catégorie est requis');
      return;
    }

    try {
      // Ne mettre à jour que les champs de nom et description
      const categoryData = {
        categoryNameFR: formData.categoryName,
        categoryNameEN: formData.categoryNameEN || '',
        categoryNameES: formData.categoryNameES || '',
        descriptionFR: formData.descriptionFR || '',
        descriptionEN: formData.descriptionEN || '',
        descriptionES: formData.descriptionES || '',
      };

      if (editingCategory) {
        // Mise à jour d'une catégorie existante - ne pas toucher aux sous-catégories
        await updateDoc(doc(db, 'analysis_categories', editingCategory.id), categoryData);
      } else {
        // Nouvelle catégorie - initialiser les sous-catégories avec un objet vide
        await addDoc(collection(db, 'analysis_categories'), {
          ...categoryData,
          subcategories: {}
        });
      }

      handleCloseDialog();
      fetchCategories();
    } catch (err) {
      console.error("Error saving category:", err);
      setError("Erreur lors de l'enregistrement de la catégorie");
    }
  };

  const handleSaveSubCategory = async () => {
    if (!editingSubCategory) return;

    try {
      const categoryRef = doc(db, 'analysis_categories', editingSubCategory.categoryId);
      const categoryDoc = await getDoc(categoryRef);
      const categoryData = categoryDoc.data();
      const subcategories = categoryData?.subcategories || {};

      const newSubCategory = {
        subcategoryName: subCategoryFormData.name,
        prompt: subCategoryFormData.description,
        descriptionFR: subCategoryFormData.descriptionFR,
        descriptionEN: subCategoryFormData.descriptionEN,
        descriptionES: subCategoryFormData.descriptionES
      };

      if (editingSubCategory.subCategoryId) {
        subcategories[editingSubCategory.subCategoryId] = newSubCategory;
      } else {
        const newId = Date.now().toString();
        subcategories[newId] = newSubCategory;
      }

      await updateDoc(categoryRef, { subcategories });
      handleCloseSubCategoryDialog();
      fetchCategories();
    } catch (error) {
      console.error("Error saving subcategory:", error);
      setError("Erreur lors de l'enregistrement de la sous-catégorie");
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await deleteDoc(doc(db, 'analysis_categories', categoryId));
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
        setError("Erreur lors de la suppression de la catégorie");
      }
    }
  };

  const handleDeleteSubCategory = async (categoryId: string, subCategoryId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-catégorie ?')) {
      try {
        const categoryRef = doc(db, 'analysis_categories', categoryId);
        const categoryDoc = await getDoc(categoryRef);
        const categoryData = categoryDoc.data();
        const subcategories = { ...categoryData?.subcategories };
        delete subcategories[subCategoryId];
        await updateDoc(categoryRef, { subcategories });
        fetchCategories();
      } catch (error) {
        console.error("Error deleting subcategory:", error);
        setError("Erreur lors de la suppression de la sous-catégorie");
      }
    }
  };

  const handleExpandCategory = (categoryId: string) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
       
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'var(--cancel-button-gradient)',
            color: 'black',
            fontWeight: 500,
            '&:hover': {
              background: '#68f1d1',
            },
          }}
        >
          Nouvelle Catégorie
        </Button>
      </Box>

      <List>
        {categories.map((category) => (
          <Box
            key={category.id}
            sx={{
              mb: 2,
              borderRadius: 1,
              overflow: 'hidden',
              border: '10px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Box
              onClick={() => handleExpandCategory(category.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                background: 'linear-gradient(45deg, #1a1b35 30%, #2a2b45 90%)',
                cursor: 'pointer',
                '&:hover': {
                  background: 'linear-gradient(45deg, #2a2b45 30%, #3a3b55 90%)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <IconButton
                  size="small"
                  sx={{
                    transform: expandedCategories.includes(category.id) ? 'rotate(-180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s',
                    color: '#68f1d1',
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
                <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
                  {category.categoryNameFR}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDialog(category);
                  }}
                  sx={{
                    background: '#4ac9ab',
                    color: '#1a1b35',
                    fontWeight: 500,
                    '&:hover': {
                      background: '#e6ff02ff',
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(category.id);
                  }}
                  sx={{
                    background: '#4ac9ab',
                    color: '#1a1b35',
                    fontWeight: 500,
                    '&:hover': {
                      background: '#68f1d1',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Collapse in={expandedCategories.includes(category.id)} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#68f1d1' }}>
                    Sous-catégories
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSubCategoryDialog(category.id)}
                    sx={{
                      mt: 2,
                      background: '#4ac9ab',
                      color: '#1a1b35',
                      fontWeight: 500,
                      '&:hover': {
                        background: '#68f1d1',
                      },
                    }}
                  >
                    Nouvelle sous-catégorie
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {Object.entries(category.subcategories || {}).map(([subCatId, subCat]) => (
                    <Grid item xs={12} sm={6} md={4} key={subCatId}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          background: 'var(--fd-gridBLOG)',
                          borderRadius: '15px'
                        }}
                      >
                        <Box sx={{  justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 500 }}>
                              {subCat.subcategoryName}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.7)', 
                                mt: 0.5,
                                mb: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {subCat.prompt}
                            </Typography>
                          </Box>
                          <Box sx={{ ml: 1, display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenSubCategoryDialog(
                                  category.id, 
                                  subCatId, 
                                  { 
                                    subcategoryName: subCat.subcategoryName, 
                                    prompt: subCat.prompt,
                                    descriptionFR: subCat.descriptionFR,
                                    descriptionEN: subCat.descriptionEN,
                                    descriptionES: subCat.descriptionES
                                  }
                                );
                              }}
                              sx={{
                                background: '#4ac9ab',
                                color: '#1a1b35',
                                fontWeight: 500,
                                '&:hover': {
                                  background: '#68f1d1',
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSubCategory(category.id, subCatId);
                              }}
                              sx={{
                                background: '#4ac9ab',
                                color: '#1a1b35',
                                fontWeight: 500,
                                '&:hover': {
                                  background: '#68f1d1',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Collapse>
          </Box>
        ))}
      </List>

      {/* Dialog pour les catégories */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'var(--Fond-carte)',
            color: 'white',
            minHeight: '60vh',
          }
        }}
      >
        <DialogTitle>
          {editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                autoFocus
                margin="dense"
                label="Nom de la catégorie (FR)"
                fullWidth
                value={formData.categoryName}
                onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', 
                    background: 'var(--dark-blue)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="Category Name (EN)"
                fullWidth
                value={formData.categoryNameEN || ''}
                onChange={(e) => setFormData({ ...formData, categoryNameEN: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', 
                    background: 'var(--dark-blue)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="Nombre de categoría (ES)"
                fullWidth
                value={formData.categoryNameES || ''}
                onChange={(e) => setFormData({ ...formData, categoryNameES: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', 
                    background: 'var(--dark-blue)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="Description (FR)"
                fullWidth
                multiline
                rows={4}
                value={formData.descriptionFR}
                onChange={(e) => setFormData({ ...formData, descriptionFR: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', 
                    background: 'var(--dark-blue)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="Description (EN)"
                fullWidth
                multiline
                rows={4}
                value={formData.descriptionEN}
                onChange={(e) => setFormData({ ...formData, descriptionEN: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', 
                    background: 'var(--dark-blue)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)', 
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                margin="dense"
                label="Descripción (ES)"
                fullWidth
                multiline
                rows={4}
                value={formData.descriptionES}
                onChange={(e) => setFormData({ ...formData, descriptionES: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', 
                    background: 'var(--dark-blue)',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.23)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              background: '#4ac9ab',
              color: '#1a1b35',
              fontWeight: 500,
              '&:hover': {
                background: '#68f1d1',
              },
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            disabled={!formData.categoryName.trim()}
            sx={{
              background: '#4ac9ab',
              color: '#1a1b35',
              fontWeight: 500,
              '&:hover': {
                background: '#68f1d1',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {editingCategory ? "Modifier" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour les sous-catégories */}
      <Dialog 
        open={openSubCategoryDialog} 
        onClose={handleCloseSubCategoryDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1a1b35',
            color: 'white',
            minHeight: '60vh',
          }
        }}
      >
        <DialogTitle>
          {editingSubCategory?.subCategoryId ? "Modifier la sous-catégorie" : "Nouvelle sous-catégorie"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Paper elevation={3} sx={{ p: 2, backgroundColor: 'rgba(5, 255, 255, 0.5)', borderRadius: '36px' }}>
              <TextField
                autoFocus
                margin="dense"
                label="Nom de la sous-catégorie"
                fullWidth
                value={subCategoryFormData.name}
                onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, name: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    borderRadius: '22px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.33)' },
                    '&:hover fieldset': { borderColor: '#68f1d1' },
                    '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': { color: '#68f1d1' },
                  }
                }}
              />
            </Paper>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            {/* Colonne de gauche - 100% sur mobile, 75% sur desktop */}
            <Box sx={{ 
              flex: { xs: '1 1 100%', md: '0 0 75%' },
              minWidth: 0 // Permet au contenu de se rétrécir si nécessaire
            }}>
              <Paper elevation={3} sx={{ p: 2, backgroundColor: 'rgba(55, 255, 255, 0.1)', borderRadius: '30px' }}>
                <TextField
                  margin="dense"
                  label="Prompt"
                  fullWidth
                  multiline
                  minRows={12}
                  maxRows={15}
                  value={subCategoryFormData.description}
                  onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, description: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      borderRadius: '22px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: '#68f1d1' },
                      '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': { color: '#68f1d1' },
                    },
                    '& .MuiInputBase-root': {
                      resize: 'vertical',
                      overflow: 'auto',
                    }
                  }}
                />
              </Paper>
            </Box>

            {/* Colonne droite - 100% sur mobile, 25% sur desktop */}
            <Box sx={{ 
              flex: { xs: '1 1 100%', md: '0 0 25%' },
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              minWidth: 0 // Permet au contenu de se rétrécir si nécessaire
            }}>
              <Paper elevation={3} sx={{ p: 1, backgroundColor: 'rgba(255, 55, 255, 0.25)', borderRadius: '20px' }}>
                <TextField
                  margin="dense"
                  label="Description / FR"
                  fullWidth
                  multiline
                  rows={4}
                  value={subCategoryFormData.descriptionFR}
                  onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, descriptionFR: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: '#68f1d1' },
                      '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': { color: '#68f1d1' },
                    }
                  }}
                />
              </Paper>

              <Paper elevation={3} sx={{ p: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '20px' }}>
                <TextField
                  margin="dense"
                  label="Description / ENG"
                  fullWidth
                  multiline
                  rows={4}
                  value={subCategoryFormData.descriptionEN}
                  onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, descriptionEN: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: '#68f1d1' },
                      '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': { color: '#68f1d1' },
                    }
                  }}
                />
              </Paper>

              <Paper elevation={3} sx={{ p: 1, backgroundColor: 'rgba(255, 255, 55, 0.15)', borderRadius: '20px' }}>
                <TextField
                  margin="dense"
                  label="Description / ESP"
                  fullWidth
                  multiline
                  rows={4}
                  value={subCategoryFormData.descriptionES}
                  onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, descriptionES: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                      '&:hover fieldset': { borderColor: '#68f1d1' },
                      '&.Mui-focused fieldset': { borderColor: '#68f1d1' },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': { color: '#68f1d1' },
                    }
                  }}
                />
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseSubCategoryDialog}
            sx={{
              background: '#4ac9ab',
              color: '#1a1b35',
              fontWeight: 500,
              '&:hover': {
                background: '#68f1d1',
              },
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSubCategory}
            disabled={!subCategoryFormData.name.trim()}
            sx={{
              background: '#4ac9ab',
              color: '#1a1b35',
              fontWeight: 500,
              '&:hover': {
                background: '#68f1d1',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {editingSubCategory?.subCategoryId ? "Modifier" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
